import { openProfileDocument } from "../../../backend/transport";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DropDown, SearchBar, TitleBar } from '../../common';
import {
  BUTTON,
  DISPATCH_COLUMN,
  DISPLAY_TEXTS,
  PKJ_STATUS,
  POPUP_MSG,
  POPUP_TYPE,
  ROUTES,
  TITLES,
} from '../../../constants';
import { Button, Input, PopUpBox } from '../../shared';
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../../backend/navigation";
import { StatusBox } from '../manufacturing/clothManufacturing/StatusBox';
import { Scanner } from '../../common/Scanner';
import { useDebounce } from '../../../hooks';
import { getLocalStorageItem } from '../../../utils/localStorage.js';
import { popup } from '../../../utils';
import {
  vendorDeleteDispatch,
  vendorSubmitDispatch,
  getPreScannedPackages,
  addPackageToDispatch,
  vendorUpdateDispatchDetail,
  printPackingListDispatch,
  vendorBill,
} from '../../../service';
import { useLoader } from '../../../context/LoaderContext';
import { shipmentDataCompiler } from '../../../utils/dataCompiler/dispatchDataCompiler';
import { BlpTilesDispatch } from '../../common/BlpTilesDispatch';
import scan_success from '../../../assets/sounds/scan_success.mp3';
import scan_failed from '../../../assets/sounds/error.mp3';
import scan_duplicate from '../../../assets/sounds/duplicate.mp3';

export const CreateDispatch = () => {
  const { setLoading } = useLoader();
  const { state } = useLocation();
  const [tilesToDisplay, setTilesToDisplay] = useState([]);
  const [scanData, setScanData] = useState('');
  const [scanning, setScanning] = useState(false);
  const [reload, setReload] = useState(false);
  const [validate, setValidate] = useState(false);
  const [preScannedPackages, setPreScannedPackages] = useState([]);
  const [isCamScannerOpen, setIsCamScannerOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState(false);
  const [shipmentData, setShipmentData] = useState({
    warehouse_id: '',
    warehouse_name: '',
    invoice_no: '',
    physical_container_no: '',
    warehouse_options: [],
  });
  const isCameraScan = localStorage.getItem('isCameraScan');
  const isCameraScanBool = isCameraScan === 'true';
  const [isShipmentDataLoading, setIsShipmentDataLoading] = useState(false);
  const [isScanningLoading, setIsScanningLoading] = useState(false);
  const [isValidateLoading, setIsValidateLoading] = useState(false);
  const [containerId, setContainerId] = useState(shipmentData.invoice_no || '');
  const [sort1, setSort1] = useState('Box Type');
  const [sortingArray1, setSortingArray1] = useState(['Single Box', 'Mix Box']);
  let duplicateAudio = useMemo(() => new Audio(scan_success), []);
  let failedAudio = useMemo(() => new Audio(scan_failed), []);
  let successAudio = useMemo(() => new Audio(scan_duplicate), []);

  useEffect(() => {
    if (isCameraScanBool && !isCamScannerOpen) setScanning(false);
  }, [isCamScannerOpen, isCameraScanBool]);

  const getAlreadyScannedPackages = useDebounce((searchData) => {
    setIsShipmentDataLoading(true);
    getPreScannedPackages({ data: { picking_ids: state?.id, ...searchData, page_number: 1 } })
      .then((response) => {
        setIsShipmentDataLoading(false);
        if (!response) return;
        if (response?.data?.records?.length > 0) {
          setPreScannedPackages(shipmentDataCompiler(response?.data?.records?.[0]));
            setShipmentData({
              warehouse_id: response?.data?.records?.[0]?.warehouse_id?.id,
              warehouse_name: response?.data?.records?.[0]?.warehouse_id?.name,
              invoice_no: response?.data?.records?.[0]?.invoice_no || '',
              physical_container_no: response?.data?.records?.[0]?.physical_container_no || '',
              warehouse_options: response?.data?.records?.[0]?.warehouse_ids,
            });
        } else {
          setPreScannedPackages(response.message);
        }
        setReload(false);
      })
      .catch(() => setIsShipmentDataLoading(false));
  }, 400);

  useEffect(() => {
    setIsShipmentDataLoading(true);
    getAlreadyScannedPackages();
  }, [getAlreadyScannedPackages]);

  // Only a submitted search filters the current packages. Clearing the query
  // is handled by the fetch effect below, not by package-response changes.
  useEffect(() => {
    if (search && searchTerm) {
      const filtered = preScannedPackages.filter((pkg) => {
        return (
          pkg.BLP.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pkg.SKU.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pkg.Batch.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setSearch(false);
      if (filtered.length > 0) setPreScannedPackages(filtered);
      else {
        popup(POPUP_TYPE.ERR, 'No relevant packages found');
        setSearch(false);
        setSearchTerm('');
      }
      setLoading(false);
    }
  }, [search, searchTerm, preScannedPackages, setLoading]);

  useEffect(() => {
    if (!searchTerm) {
      setIsShipmentDataLoading(true);
      getAlreadyScannedPackages();
    }
  }, [searchTerm, getAlreadyScannedPackages]);

  useEffect(() => {
    if (Array.isArray(preScannedPackages)) {
      const filteredTiles = preScannedPackages
        .map((tile) => {
          const isMixedSKU = tile?.data?.is_mixed ?? tile?.data?.package_id?.is_mixed;
          const tileClass = isMixedSKU ? 'pink-shade' : 'blue-shade';
          return { ...tile, tileClass };
        })
        .filter((tile) => {
          const isMixed = tile?.data?.is_mixed ?? tile?.data?.package_id?.is_mixed;

          if (sort1 === 'Single Box') return isMixed === false;
          if (sort1 === 'Mix Box') return isMixed === true;

          return true;
        });

      setTilesToDisplay(filteredTiles);
    }
  }, [preScannedPackages, sort1]);

  useEffect(() => {
    if (scanData) {
      setIsScanningLoading(true);

      addPackageToDispatch({
        data: {
          dispatch_id: state?.id,
          package_name: scanData,
          type: 'add',
        },
      }).then((response) => {
        setIsScanningLoading(false);
        if (response) {
          if (response?.data?.length > 0 && !response?.data[0].is_duplicate_scan) {
            setPreScannedPackages((prev) => [...prev, ...shipmentDataCompiler(response?.data[0])]);
            successAudio.play();
            popup(POPUP_TYPE.SUCCESS, response.message);
          } else if (response?.message.includes('Duplicate')) {
            duplicateAudio.play();
            popup(POPUP_TYPE.ERR, response.message);
          } else {
            failedAudio.play();
            popup(POPUP_TYPE.ERR, response?.message);
          }
        } else {
          failedAudio.play();
        }

        setLoading(false);
      });

      setScanData('');
    }
  }, [scanData, state?.id, duplicateAudio, failedAudio, successAudio, setLoading]);

  useEffect(() => {
    if (reload) {
      setIsShipmentDataLoading(true);
      getAlreadyScannedPackages();
      setReload(!reload);
    }
  }, [reload, getAlreadyScannedPackages]);

  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.DISPATCH, route: ROUTES.DISPATCH },
          { text: TITLES.CREATE_DISPATCH, route: ROUTES.NULL },
        ]}
      />
      {!isShipmentDataLoading ? (
        <ShipmentDetailsBox
          state={state}
          tilesToDisplay={tilesToDisplay}
          shipmentData={shipmentData}
          setScanning={setScanning}
          scanning={scanning}
          setIsCamScannerOpen={setIsCamScannerOpen}
          isCameraScanBool={isCameraScanBool}
          setScanData={setScanData}
          preScannedPackages={preScannedPackages}
          setValidate={setValidate}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={setSearch}
          search={search}
          getAlreadyScannedPackages={getAlreadyScannedPackages}
          printPackageApi={printPackingListDispatch}
          isScanningLoading={isScanningLoading}
          setContainerId={setContainerId}
          isValidateLoading={isValidateLoading}
          sort1={sort1}
          setSort1={setSort1}
          sortingArray1={sortingArray1}
          setSortingArray1={setSortingArray1}
        />
      ) : (
        <div className="text-center my-2">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
          <span className="ms-2">Loading...</span>
        </div>
      )}
      <div className="bg-white p-3 pt-0 package-titles-info border">
        <div className="row custom-row">
          {Array.isArray(preScannedPackages)
            ? tilesToDisplay.map((tile, index) => (
                <BlpTilesDispatch
                  key={index}
                  tile={tile}
                  dispatch_id={state?.id}
                  dispatch_status={state?.dispatch_status}
                  showLoader={scanning}
                  setReload={setReload}
                  setPreScannedPackages={setPreScannedPackages}
                />
              ))
            : typeof preScannedPackages === 'string' && (
                <div>
                  <h4 className="text-center px-1 py-5">{'Package not found'}</h4>
                </div>
              )}
        </div>
      </div>

      {validate && (
        <PopUpBox openPopUpBox={validate}>
          <DispatchSubmitValidate
            setOpenPopUpBox={setValidate}
            id={state?.id}
            containerNo={containerId}
            setIsValidateLoading={setIsValidateLoading}
          />
        </PopUpBox>
      )}
    </>
  );
};

export const AddDetails = ({
  setReload,
  groupList,
  setOpenPopUpBox,
  selectionMode,
  excludedIds,
  searchTerm,
  sort1,
  sort2,
  groupBy,
  selectedItem,
}) => {
  const [containerNo, setContainerNo] = useState('');
  const [physicalContainerNo, setPhysicalContainerNo] = useState('');
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const containerRef = useRef();
  const vendorType = getLocalStorageItem('vendor_type');

  const handleUpdate = async () => {
    try {
      let payload;

      if (selectionMode === 'all') {
        // 🔹 Build search object
        const searchObject = searchTerm
          ? {
              name: searchTerm,
              origin: searchTerm,
              'move_ids.future_lot': searchTerm,
              invoice_no: searchTerm,
              'partner_id.name': searchTerm,
              partner_ref: searchTerm,
              finished_products: searchTerm,
              physical_container_no: searchTerm,
            }
          : {};

        // 🔹 Build sort object
        const sortSelection = {
          ...(sort1 !== DISPATCH_COLUMN[11].name ? { dispatch_status: sort1.toLowerCase() } : {}),
          ...(sort2 !== DISPATCH_COLUMN[12].name ? { state: sort2.toLowerCase() } : {}),
        };

        const sortObject = {
          selection: sortSelection,
        };

        // 🔹 Handle groupBy params
        const shouldUseGroupBy = groupBy && groupBy.length > 0 && groupBy !== 'GROUP_BY';
        let groupbyParams = {
          groupby_field: shouldUseGroupBy ? groupBy : '',
          groupby_specifics: {},
        };

        if (shouldUseGroupBy && selectedItem.length) {
          if (groupBy.includes(',')) {
            const fields = groupBy.split(',');
            const groupbySpecifics = {};

            fields.forEach((field) => {
              groupbySpecifics[field.trim()] = selectedItem.map((item) => {
                if (Array.isArray(item) && item.length > 1) {
                  const id = item[0];
                  return !isNaN(Number(id)) ? Number(id) : id;
                }
                return !isNaN(Number(item)) ? Number(item) : item;
              });
            });

            groupbyParams.groupby_specifics = groupbySpecifics;
          } else {
            groupbyParams.groupby_specifics = {
              [groupBy]: selectedItem.map((item) => {
                if (Array.isArray(item) && item.length > 1) {
                  const id = item[0];
                  return !isNaN(Number(id)) ? Number(id) : id;
                }
                return !isNaN(Number(item)) ? Number(item) : item;
              }),
            };
          }
        }

        payload = {
          select_all: true,
          dispatch_id: [],
          search: searchObject,
          sort: sortObject,
          ...groupbyParams,
          excluded_ids: Array.from(excludedIds),
        };
      } else {
        const dispatch_id = groupList.map((po) => po.id);
        payload = {
          select_all: false,
          dispatch_id,
        };
      }

      // 🔹 Add extra fields
      if (containerNo) payload.invoice_no = containerNo;
      if (physicalContainerNo) payload.physical_container_no = physicalContainerNo;

      setIsSubmitLoading(true);

      const response = await vendorUpdateDispatchDetail({ data: payload });

      if (response) {
        popup(POPUP_TYPE.SUCCESS, response.message || 'Dispatch updated successfully');
        setReload(true);
        setOpenPopUpBox(false);
      }
    } catch (error) {
      popup(POPUP_TYPE.ERR, error.message || 'Something went wrong');
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    containerRef.current.focus();
  }, []);
  // To hide container no input for non clothing vendors
  let clothingVendor = false;
  if (vendorType === 'clothing') {
    clothingVendor = true;
  }
  return (
    <div className="m-2">
      <h5 className="mb-3 d-flex justify-content-center">Update Dispatch Details</h5>
      <hr />
      <div className="d-flex ps-4 gap-4">
        <div className="d-flex flex-column gap-3">
          <label className="container-label">Invoice No</label>
          {clothingVendor && <label className="container-label">Container No</label>}
        </div>
        <div className="d-flex flex-column align-items-start gap-3">
          <input
            type="text"
            placeholder="Invoice No"
            className="d-flex search-placeholder align-items-center justify-content-center container-input"
            value={containerNo}
            ref={containerRef}
            onChange={(e) => setContainerNo(e.target.value)}
          />
          {clothingVendor && (
            <Input
              type="text"
              placeholder="Container No"
              className="d-flex search-placeholder align-items-center justify-content-center container-input"
              value={physicalContainerNo}
              onChange={(e) => setPhysicalContainerNo(e.target.value)}
            />
          )}
        </div>
      </div>
      <hr />
      <div className="d-flex justify-content-end gap-3">
        <Button
          className={`search-btn ${
            ((containerNo === '' || (containerNo === undefined || containerNo === null)) &&
              (physicalContainerNo === '' || (physicalContainerNo === undefined || physicalContainerNo === null))) ||
            isSubmitLoading
              ? 'disabled'
              : ''
          } d-flex align-items-center justify-content-center px-3`}
          clickHandler={handleUpdate}
          disabled={
            ((containerNo === '' || (containerNo === undefined || containerNo === null)) &&
              (physicalContainerNo === '' || (physicalContainerNo === undefined || physicalContainerNo === null))) ||
            isSubmitLoading
          }>
          {isSubmitLoading && (
            <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
          )}
          {BUTTON.UPDATE}
        </Button>
        <Button
          className="search-btn d-flex align-items-center justify-content-center px-3"
          clickHandler={() => setOpenPopUpBox(false)}>
          {BUTTON.CANCEL}
        </Button>
      </div>
    </div>
  );
};

export const ShipmentDetailsBox = ({
  state,
  setScanData,
  scanning,
  setScanning,
  setIsCamScannerOpen,
  isCameraScanBool,
  tilesToDisplay,
  shipmentData,
  isCamScannerOpen,
  preScannedPackages,
  setValidate,
  setSearchTerm,
  searchTerm,
  setSearch,
  search,
  getAlreadyScannedPackages,
  printPackageApi,
  setCostviewOpen,
  isScanningLoading,
  setContainerId,
  isValidateLoading,
  sort1,
  setSort1,
  sortingArray1,
  // isShipmentDataLoading
}) => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const [invoiceNo, setInvoiceNo] = useState(shipmentData.invoice_no || '');
  const [containerNo, setContainerNo] = useState(shipmentData.physical_container_no || '');
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    shipmentData.warehouse_options?.find((opt) => opt.name === shipmentData.warehouse_name) || {},
  );
  const [isUpdateDetailsLoading, setIsUpdateDetailsLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isCheckBillLoading, setIsCheckBillLoading] = useState(false);
  const isDraft = state?.dispatch_status === 'draft';

  const isUpdateDetailsDisabled = () => {
    const isInvoiceUnchanged = shipmentData?.invoice_no === invoiceNo || !invoiceNo;

    const isContainerUnchanged =
      shipmentData?.physical_container_no === containerNo || !containerNo;

    const isWarehouseUnchanged = shipmentData?.warehouse_name === selectedWarehouse?.name;

    return (
      (isInvoiceUnchanged && isContainerUnchanged && isWarehouseUnchanged) || isUpdateDetailsLoading
    );
  };

  useEffect(() => {
    setInvoiceNo(shipmentData.invoice_no || '');
    setContainerId && setContainerId(shipmentData.invoice_no || '');
    setContainerNo(shipmentData.physical_container_no || '');

    const matchedWarehouse = shipmentData.warehouse_options?.find(
      (opt) => opt.name === shipmentData.warehouse_name,
    );
    setSelectedWarehouse(matchedWarehouse || {});
  }, [shipmentData, setContainerId]);

  const startScanning = () => {
    setScanning((prev) => !prev);
    setIsCamScannerOpen(isCameraScanBool);
  };
  const deleteDispatch = useDebounce(() => {
    setIsCancelLoading(true);
    vendorDeleteDispatch({ data: { dispatch_id: state?.id } })
      .then((response) => {
        setIsCancelLoading(false);
        if (!response) return;
        popup('success', response.message);
        // navigate(ROUTES.DISPATCH);
        window.history.back();
      })
      .catch(() => setIsCancelLoading(false));
  }, 400);

  const resetFilter = () => {
    setSort1('Box Type');
  };

  const handlePrintPackingList = async () => {
    popup('success', 'Print Packing List Initiated.');
    printPackageApi({
      data: { dispatch_id: state?.id },
    })
      .then((response) => {
        setLoading(false);
        if (response?.data?.length > 0) {
          openProfileDocument(response.data, '_blank', 'noreferrer');
        } else {
          popup(POPUP_TYPE.ERR, response?.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        throw err;
      });
  };

  const fetchBill = useDebounce((preScannedPackages) => {
    setIsCheckBillLoading(true);
    vendorBill({
      data: { bill_ids: [preScannedPackages?.[0].isBillAlreadyCreated], page_number: 1 },
    })
      .then((response) => {
        setIsCheckBillLoading(false);
        if (response?.data?.records?.length > 0) {
          navigate(`${ROUTES.BILLS}/${response.data.records[0].name.replaceAll('/', '_')}`, {
            state: response.data.records[0],
          });
        }
      })
      .catch(() => setIsCheckBillLoading(false));
  }, 1000);

  const handleUpdate = async () => {
    setIsUpdateDetailsLoading(true);

    const isInvoiceChanged = shipmentData.invoice_no !== invoiceNo && invoiceNo !== '';
    const isContainerChanged =
      shipmentData.physical_container_no !== containerNo && containerNo !== '';
    const isWarehouseChanged =
      shipmentData?.warehouse_name !== selectedWarehouse?.name && selectedWarehouse?.id;

    vendorUpdateDispatchDetail({
      data: {
        dispatch_id: [state?.id],
        invoice_no: isInvoiceChanged ? invoiceNo : false,
        physical_container_no: isContainerChanged ? containerNo : false,
        warehouse_id: isWarehouseChanged ? selectedWarehouse?.id : false,
      },
    })
      .then((response) => {
        if (response) {
          popup(POPUP_TYPE.SUCCESS, 'Details updated');
        }
        setContainerId && setContainerId(invoiceNo);
        setIsUpdateDetailsLoading(false);
      })
      .catch((err) => {
        setIsUpdateDetailsLoading(false);
        popup(POPUP_TYPE.ERR, err.message || 'Failed to update');
      })
      .finally(() => {
        setIsUpdateDetailsLoading(false);
        getAlreadyScannedPackages();
      });
  };

  return (
    <div className="bg-white p-3 align-items-center package-top-info border-def-up border-def-down">
      <div className="d-flex align-items-center justify-content-between">
        <div className="align-items-center justify-content-between mb-1 top-header-set">
          <h4 className="mb-0">
            {DISPLAY_TEXTS.CREATE_DISPATCH[0]}
            <span>{state?.name}</span>
          </h4>
        </div>
        <StatusBox status={PKJ_STATUS} />
      </div>
      <div className="d-flex align-items-center justify-content-between top-header-set">
        <div>
          <div className="d-flex align-items-center justify-content-start mb-2">
            <label className="container-label me-2" style={{ width: '120px' }}>
              Invoice No:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: '200px' }}
              value={invoiceNo}
              onChange={(e) => {
                setInvoiceNo(e.target.value);
              }}
              disabled={!isDraft}
            />
          </div>

          <div className="d-flex align-items-center justify-content-start mb-2">
            <label className="container-label me-2" style={{ width: '120px' }}>
              Container No:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: '200px' }}
              value={containerNo}
              onChange={(e) => setContainerNo(e.target.value)}
              disabled={!isDraft}
            />
          </div>

          <div className="d-flex align-items-center justify-content-start mb-2">
            <label className="container-label me-2" style={{ width: '120px' }}>
              Warehouse:
            </label>
            <DropDown
              dropdownMenu={shipmentData.warehouse_options?.map((opt) => opt.name) || []}
              value={selectedWarehouse.name || ''}
              setValue={(selectedName) => {
                const selectedOption = shipmentData.warehouse_options?.find(
                  (opt) => opt.name === selectedName,
                );
                setSelectedWarehouse(selectedOption || {});
              }}
              capitalize
              disabled={!isDraft}
            />
          </div>

          <div className="d-flex align-items-center">
            <h4 className="me-3 mb-4">{DISPLAY_TEXTS.CREATE_DISPATCH[1]}</h4>
            <div className="number-count mb-4 d-flex align-items-center justify-content-center">
              {tilesToDisplay?.length}
            </div>
          </div>

          {/* <div className="d-flex align-items-center justify-content-start mb-3">
            <label className="container-label">
              Finished Product :{' '}
              <span title={state?.finished_products}>
                {state?.finished_products?.length > 50
                  ? `${state?.finished_products?.slice(0, 50)}...`
                  : state?.finished_products}
              </span>
            </label>
          </div> */}

          {preScannedPackages?.length > 0 && (
            <div className="d-flex w-100 gap-3 align-items-center">
              <div style={{ flex: 1, minWidth: '350px' }}>
                <SearchBar
                  setSearchTerm={setSearchTerm}
                  searchTerm={searchTerm}
                  setSearch={setSearch}
                  search={search}
                  onDefaultSearch={getAlreadyScannedPackages}
                />
              </div>

              {sortingArray1 && (
                <div style={{ flex: 1 }} className="mb-3 filter-btns-div d-flex align-items-center">
                  <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />

                  {sortingArray1.includes(sort1) && (
                    <Button
                      clickHandler={resetFilter}
                      className="blue-btn d-flex align-items-center justify-content-center px-3">
                      {BUTTON.CLEAR_FILTERS}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="d-flex flex-column justify-content-end align-items-end">
          <div className="d-flex justify-content-end align-items-center">
            {tilesToDisplay?.length > 0 &&
              (state?.dispatch_status === 'dispatched' || state?.dispatch_status === 'received') &&
              preScannedPackages?.length > 0 &&
              preScannedPackages?.[0].isBillAlreadyCreated === false && (
                <div className="d-flex justify-content-end">
                  <Button
                    className={`blue-btn d-flex align-items-center justify-content-end px-3 py-3 me-2`}
                    clickHandler={() => setCostviewOpen(true)}>
                    {BUTTON.CREATE_BILL}
                  </Button>
                </div>
              )}
            {tilesToDisplay?.length > 0 &&
              (state?.dispatch_status === 'dispatched' || state?.dispatch_status === 'received') &&
              preScannedPackages?.length > 0 &&
              preScannedPackages?.[0].isBillAlreadyCreated && (
                <div className="d-flex justify-content-end">
                  <Button
                    className={`blue-btn d-flex align-items-center justify-content-end px-3 py-3 me-2 ${isCheckBillLoading ? 'disabled' : ''}`}
                    disabled={isCheckBillLoading}
                    clickHandler={() => {
                      setIsCheckBillLoading(true);
                      fetchBill(preScannedPackages);
                    }}>
                    {isCheckBillLoading && (
                      <span
                        className="spinner-border spinner-border-sm text-white me-2"
                        role="status"
                      />
                    )}
                    {isCheckBillLoading ? 'Checking...' : 'Check Bill'}
                  </Button>
                </div>
              )}
            {tilesToDisplay.length > 0 && (
              <div className="d-flex justify-content-end">
                <Button
                  className={`blue-btn d-flex align-items-center justify-content-end px-3 py-3 me-2`}
                  clickHandler={handlePrintPackingList}>
                  {BUTTON.PRINT_PACKING_LIST}
                </Button>
              </div>
            )}
            {state?.dispatch_status === 'draft' && (
              <div className="d-flex justify-content-end align-items-center">
                {scanning && (
                  <Scanner
                    setScanData={setScanData}
                    isCamScannerOpen={isCamScannerOpen}
                    setIsCamScannerOpen={setIsCamScannerOpen}
                  />
                )}
                {scanning && isScanningLoading && <span className="me-3">Scanning...</span>}
                <Button
                  clickHandler={startScanning}
                  className="white-btn d-flex align-items-center justify-content-center px-4">
                  {scanning ? BUTTON.STOP_SCAN : BUTTON.SCAN_BOX}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between w-100">
        {/* Left side buttons */}
        {state?.dispatch_status === 'draft' && (
          <div className="d-flex">
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 ${isUpdateDetailsDisabled() ? 'disabled' : ''}`}
              disabled={isUpdateDetailsDisabled()}
              clickHandler={handleUpdate}>
              {isUpdateDetailsLoading && (
                <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
              )}
              {isUpdateDetailsLoading ? 'Updating' : BUTTON.UPDATE_DETAILS}
            </Button>
            <Button
              clickHandler={deleteDispatch}
              disabled={isCancelLoading}
              className={`blue-btn d-flex align-items-center justify-content-center px-4 ${isCancelLoading ? 'disabled' : ''}`}>
              {isCancelLoading && (
                <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
              )}
              {isCancelLoading ? 'Cancelling..' : BUTTON.CANCEL_DISPATCH}
            </Button>
          </div>
        )}

        {/* Right side button */}
        {state?.dispatch_status === 'draft' && (
          <div className="d-flex">
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-4 ${preScannedPackages.length + tilesToDisplay.length === 0 || isValidateLoading ? 'disabled' : ''}`}
              clickHandler={() => setValidate(true)}
              disabled={
                preScannedPackages.length + tilesToDisplay.length === 0 || isValidateLoading
              }>
              {isValidateLoading && (
                <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
              )}
              {isValidateLoading ? 'Validating...' : BUTTON.SUBMIT}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const DispatchSubmitValidate = ({ setOpenPopUpBox, id, containerNo, setIsValidateLoading }) => {
  // const navigate = useNavigate();
  const { setLoading } = useLoader();

  const closePopUp = () => {
    setOpenPopUpBox(false);
  };

  const handleSubmitDispatch = () => {
    setIsValidateLoading(true);
    setOpenPopUpBox(false);
    vendorSubmitDispatch({
      data: { dispatch_id: [id] },
    })
      .then((response) => {
        setIsValidateLoading(false);
        if (!response) return;
        popup('success', response.message);
        // navigate(ROUTES.DISPATCH);
        window.history.back();
      })
      .catch(() => {
        setIsValidateLoading(false);
        setLoading(false);
      });
  };

  const handleSaveAsDraft = () => {
    popup('success', 'Dispatch Saved as Draft');
    // navigate(ROUTES.DISPATCH);
    window.history.back();
  };

  return (
    <>
      <div className="d-flex m-1">
        <div className="w-25 modal-body py-0 d-flex justify-content-center align-items-center">
          <span className="d-flex justify-content-center align-items-center bg-info badge">!</span>
        </div>
        <div className="w-75 modal-body py-0">
          <h4 className="text-center text-special mb-3">
            <u>NOTE</u>
          </h4>
          <p className="text-center text-special placeholder-color">
            {POPUP_MSG.SUBMIT_DISPATCH_WARNING}
          </p>
        </div>
      </div>
      <hr />
      <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4 modal-btn custom-button">
        <div className="d-flex align-items-center justify-content-between">
          <Button
            clickHandler={handleSubmitDispatch}
            className={`border-0 m-1 ${containerNo === '' || containerNo === false || containerNo === 'N/A' ? 'disabled' : ''} blue-btn d-flex align-items-center justify-content-center py-2 px-3`}
            disabled={containerNo === '' || containerNo === false || containerNo === 'N/A'}>
            {BUTTON.SUBMIT}
          </Button>
          <Button
            clickHandler={handleSaveAsDraft}
            className="border-0 m-1 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.SAVE_AS_DRAFT}
          </Button>
          <Button
            clickHandler={closePopUp}
            className="border-0 m-1 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      </div>
    </>
  );
};
